<template>
  <div
    ref="containerRef"
    class="relative flex grow flex-col overflow-hidden bg-gray-200"
  >
    <GridBackground class="absolute inset-0 z-0" />

    <div
      class="z-10 grid grow content-center justify-center"
      style="
        grid-template-columns: 1rem min-content 1rem;
        grid-template-rows: 1rem min-content 1rem;
      "
    >
      <div></div>
      <div></div>
      <div></div>

      <div></div>
      <div
        class="overflow-hidden bg-white box-content border border-gray-300 shadow-lg"
        :style="{
          width: Math.round(computedSize.width * computedSize.zoom) + 'px',
          height: Math.round(computedSize.height * computedSize.zoom) + 'px',
        }"
      >
        <div
          class="flex origin-top flex-col"
          :class="[dragState && `pointer-events-none`]"
          :style="{
            width: computedSize.width + 'px',
            height: computedSize.height + 'px',
            marginLeft:
              (computedSize.width -
                Math.round(computedSize.width * computedSize.zoom)) /
                -2 +
              'px',
            scale: computedSize.zoom,
          }"
        >
          <slot></slot>
        </div>
      </div>
      <div
        class="cursor-e-resize"
        @mousedown="handleRightHandle"
        @touchstart="handleRightHandle"
      ></div>

      <div></div>
      <div
        class="cursor-s-resize"
        @mousedown="handleBottomHandle"
        @touchstart="handleBottomHandle"
      ></div>
      <div
        class="cursor-se-resize text-gray-800"
        @mousedown="handleBottomRightHandle"
        @touchstart="handleBottomRightHandle"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="1em"
          width="1em"
          viewBox="0 0 36 36"
          style="margin-left: -4px; margin-top: -4px"
        >
          <circle cx="12" cy="24" r="1.5" fill="currentColor" />
          <circle cx="18" cy="24" r="1.5" fill="currentColor" />
          <circle cx="18" cy="18" r="1.5" fill="currentColor" />
          <circle cx="24" cy="12" r="1.5" fill="currentColor" />
          <circle cx="24" cy="24" r="1.5" fill="currentColor" />
          <circle cx="24" cy="18" r="1.5" fill="currentColor" />
          <path fill="none" d="M0 0h36v36H0z" />
        </svg>
      </div>
    </div>

    <div
      class="absolute bottom-0 left-0 z-20 flex items-center justify-center gap-1 p-2"
    >
      <span
        class="flex h-6 items-center gap-1 whitespace-nowrap rounded border border-gray-300 bg-white px-2 text-xs font-medium tabular-nums text-gray-800"
      >
        <span>{{ computedSize.width }}</span>
        <span>&#215;</span>
        <span>{{ computedSize.height }}</span>
        <span class="text-gray-500">
          ({{ Math.round(100 * computedSize.zoom) }}%)
        </span>
      </span>

      <button
        type="button"
        aria-label="Rotate orientation"
        class="grid h-6 w-6 place-items-center rounded border border-gray-300 bg-white text-gray-800 transition-colors hover:bg-gray-300"
        @click="handleRotateOrientation"
      >
        <ArrowPathRoundedSquareIcon class="h-4 w-4" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onWatcherCleanup, ref, shallowRef, watch } from "vue";

import ArrowPathRoundedSquareIcon from "@/icons/heroicons/arrow-path-rounded-square-micro.svg";

import { ResponsiveSize } from "../responsive";
import GridBackground from "./GridBackground.vue";

type DragState =
  | {
      source: "right";
      width: number;
      x: number;
    }
  | {
      source: "bottom";
      height: number;
      y: number;
    }
  | {
      source: "bottom-right";
      width: number;
      height: number;
      x: number;
      y: number;
    };

const containerRef = ref<HTMLElement>();

const dragState = shallowRef<DragState>();

const containerSize = shallowRef({ width: 0, height: 0 });
const responsiveSize = defineModel<ResponsiveSize>("responsiveSize", {
  required: true,
});

const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

const computedSize = computed(() => {
  const { width: cW, height: cH } = containerSize.value;
  const { width: rW, height: rH } = responsiveSize.value;

  const calculate = (rD: number, cD: number, p: number) => {
    const zoom = rD > cD - p ? (cD - p) / rD : 1;

    const size = Math.min(
      clamp(Math.round(rD / zoom), 50, 8_000),
      Math.round((cD - p) / zoom)
    );

    return [size, zoom];
  };

  // 16px = gap/resize handles
  const [w, wZ] = calculate(rW, cW, 16 * 2);
  const [h, hZ] = calculate(rH, cH, 16 * 2);

  return {
    width: w,
    height: h,
    zoom: Math.min(wZ, hZ),
  };
});

const getCoords = (ev: MouseEvent | TouchEvent) => {
  if ("targetTouches" in ev) {
    if (ev.targetTouches.length === 1) {
      const touch = ev.targetTouches[0];

      return { x: touch.clientX, y: touch.clientY };
    }
  } else {
    return { x: ev.clientX, y: ev.clientY };
  }

  return null;
};

const handleRightHandle = (ev: MouseEvent | TouchEvent) => {
  const coords = getCoords(ev);

  if (coords !== null) {
    ev.preventDefault();

    dragState.value = {
      source: "right",
      width: computedSize.value.width,
      x: coords.x,
    };
  }
};

const handleBottomHandle = (ev: MouseEvent | TouchEvent) => {
  const coords = getCoords(ev);

  if (coords !== null) {
    ev.preventDefault();

    dragState.value = {
      source: "bottom",
      height: computedSize.value.height,
      y: coords.y,
    };
  }
};

const handleBottomRightHandle = (ev: MouseEvent | TouchEvent) => {
  const coords = getCoords(ev);

  if (coords !== null) {
    ev.preventDefault();

    dragState.value = {
      source: "bottom-right",
      width: computedSize.value.width,
      height: computedSize.value.height,
      x: coords.x,
      y: coords.y,
    };
  }
};

const handleRotateOrientation = () => {
  responsiveSize.value = {
    key: responsiveSize.value.key,
    width: responsiveSize.value.height,
    height: responsiveSize.value.width,
  };
};

watch(containerRef, (containerEl) => {
  if (!containerEl) {
    return;
  }

  const observer = new ResizeObserver(() => {
    const rect = containerEl.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);

    containerSize.value = { width, height };
  });

  observer.observe(containerEl);
  onWatcherCleanup(() => observer.disconnect());
});

watch(dragState, (drag) => {
  if (!drag) {
    return;
  }

  const handle = drag.source;

  const handleMove = (ev: MouseEvent | TouchEvent) => {
    ev.preventDefault();

    const coords = getCoords(ev);
    if (coords === null) {
      return;
    }

    if (handle === "right") {
      responsiveSize.value = {
        height: responsiveSize.value.height,
        width: drag.width + 2 * (coords.x - drag.x),
      };
    } else if (handle === "bottom") {
      responsiveSize.value = {
        width: responsiveSize.value.width,
        height: drag.height + 2 * (coords.y - drag.y),
      };
    } else if (handle === "bottom-right") {
      responsiveSize.value = {
        width: drag.width + 2 * (coords.x - drag.x),
        height: drag.height + 2 * (coords.y - drag.y),
      };
    }
  };

  const handleRelease = (ev: MouseEvent | TouchEvent) => {
    ev.preventDefault();
    dragState.value = undefined;
  };

  window.addEventListener("mousemove", handleMove);
  window.addEventListener("mouseup", handleRelease);
  window.addEventListener("touchmove", handleMove);
  window.addEventListener("touchend", handleRelease);

  onWatcherCleanup(() => {
    window.removeEventListener("mousemove", handleMove);
    window.removeEventListener("mouseup", handleRelease);
    window.removeEventListener("touchmove", handleMove);
    window.removeEventListener("touchend", handleRelease);
  });
});
</script>
